import type { PulseRole } from '@/lib/vision/schemas'

/** Friendly labels for the Vision / Entra role ladder. */
export const PULSE_ROLE_LABEL: Record<PulseRole, string> = {
  BoldfitSuperadmin: 'Superadmin',
  BoldfitAdmin: 'Admin',
  BoldfitConfigManager: 'Config Manager',
  BoldfitManager: 'Operator',
  BoldfitViewer: 'Viewer',
}

/** Highest first — matches Vision `PulseRole` ordinal order. */
export const PULSE_ROLES: readonly PulseRole[] = [
  'BoldfitSuperadmin',
  'BoldfitAdmin',
  'BoldfitConfigManager',
  'BoldfitManager',
  'BoldfitViewer',
] as const

export function pulseRoleLabel(role: PulseRole): string {
  return PULSE_ROLE_LABEL[role]
}

export function isAdminTier(role: PulseRole | undefined): boolean {
  return role === 'BoldfitSuperadmin' || role === 'BoldfitAdmin'
}

export function isSuperadmin(role: PulseRole | undefined): boolean {
  return role === 'BoldfitSuperadmin'
}

/**
 * Config Manager+ — MSL Excel download / upload.
 * Centralised so nav, route guards, and hooks share one rule.
 */
export function canManageMsl(role: PulseRole | undefined): boolean {
  return role === 'BoldfitSuperadmin' || role === 'BoldfitAdmin' || role === 'BoldfitConfigManager'
}

/** Operator+ can ACK / warehouse-release. Viewer and unknown cannot. */
export function canWriteOms(role: PulseRole | undefined): boolean {
  return (
    role === 'BoldfitSuperadmin' ||
    role === 'BoldfitAdmin' ||
    role === 'BoldfitConfigManager' ||
    role === 'BoldfitManager'
  )
}

/** Same Operator+ gate from an Entra / DEV JWT `roles` claim. */
export function canWriteOmsFromRoles(roles: string[]): boolean {
  return (
    roles.includes('BoldfitSuperadmin') ||
    roles.includes('BoldfitAdmin') ||
    roles.includes('BoldfitConfigManager') ||
    roles.includes('BoldfitManager')
  )
}

export function canManageMslFromRoles(roles: string[]): boolean {
  return (
    roles.includes('BoldfitSuperadmin') ||
    roles.includes('BoldfitAdmin') ||
    roles.includes('BoldfitConfigManager')
  )
}

/** Shown on greyed write controls. Uses existing Viewer / Operator labels. */
export const VIEWER_WRITE_HINT = 'Viewer — ask an Operator to acknowledge'

/** Cancel is Admin+ only — not the Operator ACK path. */
export const CANCEL_GRANT_HINT = 'Cancel needs an Admin role in Shield'

/**
 * Create BC SO is Admin+ for now. The feature stays; Operators were creating
 * duplicate Sales Orders from this CTA, so the press is parked behind the
 * existing Superadmin/Admin check until that is safe to reopen.
 */
export const CREATE_BC_SO_ADMIN_HINT = 'Create BC SO is Admin-only for now'

export const MSL_MANAGE_HINT = 'MSL needs a Config Manager or Admin role in Shield'

export function rolesFromClaims(value: unknown): string[] {
  if (!value || typeof value !== 'object') return []
  const roles = (value as { roles?: unknown }).roles
  if (!Array.isArray(roles)) return []
  return roles.filter((role): role is string => typeof role === 'string')
}

export function rolesFromAccessToken(token: string): string[] {
  const payload = token.split('.')[1]
  if (!payload) return []
  try {
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    return rolesFromClaims(JSON.parse(json))
  } catch {
    return []
  }
}
